import { useState } from "react";
import useIsMobile from "@/hooks/useIsMobile";
import useWebview from "@/webview/useWebview";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { ElementType } from "@/constants/tagular/main";
import { CardType } from "../../ProgramCarousel";
import { EnrichedProgram } from "@/common/course/types";
import CardWrapper from "../../ProgramCarousel/components/CardWrapper";
import useTranslations from "@/i18n/useTranslations";

type ProgramCardListProps = {
  cardType?: CardType;
  showMoreLabel?: string;
  showLessLabel?: string;
  programs: EnrichedProgram[];
};

export const ProgramCardList = ({
  programs,
  cardType = "program-card",
  showMoreLabel,
  showLessLabel,
}: ProgramCardListProps) => {
  const t = useTranslations("Course");
  const [isExpanded, setIsExpanded] = useState(false);

  const resolvedShowMoreLabel = showMoreLabel ?? t("showMorePrograms");
  const resolvedShowLessLabel = showLessLabel ?? t("showLessPrograms");

  const isMobile = useIsMobile();
  const { getUrl } = useWebview();

  const programList = isExpanded ? programs : programs.slice(0, 3);

  const alignment = programs.length === 1 && !isMobile ? "row" : "column";
  const gridStyles =
    programs.length < 3
      ? "grid-cols-[repeat(auto-fit,minmax(250px,1fr))]"
      : "lg:grid-cols-3";

  const cardStyles = {
    "product-card": "min-h-[370px]",
    "program-card": "flex flex-1",
    "new-product-card": "",
  };

  const eventing = isExpanded
    ? {
        htmlId: "related_programs_show_less_clicked",
        name: "related_programs_show_less_clicked",
        text: resolvedShowLessLabel,
      }
    : {
        htmlId: "related_programs_show_more_clicked",
        name: "related_programs_show_more_clicked",
        text: resolvedShowMoreLabel,
      };

  const { elementClicked } = useElementEvents({
    webElement: {
      elementType: ElementType.Button,
      htmlId: eventing.htmlId,
      location: "course detail",
      name: eventing.name,
      text: eventing.text,
    },
  });

  return (
    <>
      <div className={`grid gap-6 ${gridStyles}`}>
        {programList.map((program, index) => (
          <CardWrapper
            index={index}
            item={program}
            getUrl={getUrl}
            key={program.uuid}
            cardType={cardType}
            alignment={alignment}
            className={cardStyles[cardType]}
          />
        ))}
      </div>
      {programs.length > 3 && (
        <button
          type="button"
          onClick={() => {
            elementClicked();
            setIsExpanded(!isExpanded);
          }}
          className="block mx-auto mt-10 text-lg text-primary-500 underline underline-offset-[3px]"
        >
          {isExpanded ? resolvedShowLessLabel : resolvedShowMoreLabel}
        </button>
      )}
    </>
  );
};
